import { apiClient } from './apiClient';
import { knowledgeService } from './knowledgeService';
import { reviewService } from './reviewService';
import {
  CORE_SUBJECT_CODES,
  SUBJECT_CONFIG,
  type SubjectCode,
  normalizeSubjectCode,
} from '../utils/subjectConfig';

export type ParticleCoverageSubjectEntry = {
  subject: SubjectCode;
  totalParticles: number;
  reviewedParticles: number;
  coveragePercent: number;
  sessionCount: number;
  footprintCount: number;
};

export type ParticleCoverageSnapshot = {
  overall: {
    totalParticles: number;
    reviewedParticles: number;
    coveragePercent: number;
    reviewRoundCount: number;
  };
  bySubject: ParticleCoverageSubjectEntry[];
};

const SUBJECT_QUERY_LABEL: Record<SubjectCode, string> = {
  yuwen: '语文',
  math: '数学',
  english: '英语',
  other: '其他',
};

function computeCoveragePercent(reviewed: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return Math.min(100, Math.round((reviewed / total) * 100));
}

function normalizeCoverageSnapshot(raw: ParticleCoverageSnapshot): ParticleCoverageSnapshot {
  const bySubject = CORE_SUBJECT_CODES.map((subjectCode) => {
    const existing = raw.bySubject.find((entry) => normalizeSubjectCode(entry.subject) === subjectCode);
    return {
      subject: subjectCode,
      totalParticles: existing?.totalParticles ?? 0,
      reviewedParticles: existing?.reviewedParticles ?? 0,
      coveragePercent: existing?.coveragePercent ?? 0,
      sessionCount: existing?.sessionCount ?? 0,
      footprintCount: existing?.footprintCount ?? 0,
    };
  });

  const overallTotalParticles = bySubject.reduce((sum, entry) => sum + entry.totalParticles, 0);
  const overallReviewedParticles = typeof raw.overall?.reviewedParticles === 'number'
    ? raw.overall.reviewedParticles
    : bySubject.reduce((sum, entry) => sum + entry.reviewedParticles, 0);
  const reviewRoundCount = typeof raw.overall?.reviewRoundCount === 'number'
    ? raw.overall.reviewRoundCount
    : bySubject.reduce((sum, entry) => sum + entry.sessionCount, 0);

  return {
    overall: {
      totalParticles: overallTotalParticles,
      reviewedParticles: overallReviewedParticles,
      coveragePercent: computeCoveragePercent(overallReviewedParticles, overallTotalParticles),
      reviewRoundCount,
    },
    bySubject,
  };
}

async function loadParticleCoverageFallback(userId: string): Promise<ParticleCoverageSnapshot> {
  const [particleTotals, history] = await Promise.all([
    Promise.all(
      CORE_SUBJECT_CODES.map(async (subjectCode) => {
        const page = await knowledgeService.listKnowledgeParticles({
          userId,
          subject: SUBJECT_QUERY_LABEL[subjectCode],
          page: 1,
          pageSize: 1,
        });
        return {
          subjectCode,
          totalParticles: page.pagination.total,
        };
      }),
    ),
    reviewService.loadSessionHistory(userId, { limit: 180 }),
  ]);

  const reviewedBySubject = new Map<SubjectCode, Set<string>>();
  const footprintCountBySubject = new Map<SubjectCode, number>();
  const sessionCountBySubject = new Map<SubjectCode, number>();

  for (const subjectCode of CORE_SUBJECT_CODES) {
    reviewedBySubject.set(subjectCode, new Set());
    footprintCountBySubject.set(subjectCode, 0);
    sessionCountBySubject.set(subjectCode, 0);
  }

  for (const entry of history) {
    if (entry.status !== 'completed') {
      continue;
    }
    const subjectCode = normalizeSubjectCode(entry.subject);
    sessionCountBySubject.set(subjectCode, (sessionCountBySubject.get(subjectCode) || 0) + 1);
  }

  const overallReviewed = new Set<string>();

  for (const subjectCode of CORE_SUBJECT_CODES) {
    let offset = 0;
    const pageSize = 300;
    let hasMore = true;

    while (hasMore) {
      const snapshot = await reviewService.loadFamilyFootprint(userId, {
        subject: subjectCode,
        limit: pageSize,
        offset,
      });

      for (const row of snapshot.rows) {
        reviewedBySubject.get(subjectCode)?.add(row.particleId);
        overallReviewed.add(row.particleId);
      }

      footprintCountBySubject.set(
        subjectCode,
        (footprintCountBySubject.get(subjectCode) || 0) + snapshot.rows.length,
      );

      offset += snapshot.rows.length;
      hasMore = snapshot.rows.length >= pageSize && offset < snapshot.total;
    }
  }

  const bySubject: ParticleCoverageSubjectEntry[] = particleTotals.map(({ subjectCode, totalParticles }) => {
    const reviewedParticles = reviewedBySubject.get(subjectCode)?.size ?? 0;
    return {
      subject: subjectCode,
      totalParticles,
      reviewedParticles,
      coveragePercent: computeCoveragePercent(reviewedParticles, totalParticles),
      sessionCount: sessionCountBySubject.get(subjectCode) || 0,
      footprintCount: footprintCountBySubject.get(subjectCode) || 0,
    };
  });

  const overallTotalParticles = bySubject.reduce((sum, entry) => sum + entry.totalParticles, 0);
  const overallReviewedParticles = overallReviewed.size;
  const reviewRoundCount = bySubject.reduce((sum, entry) => sum + entry.sessionCount, 0);

  return {
    overall: {
      totalParticles: overallTotalParticles,
      reviewedParticles: overallReviewedParticles,
      coveragePercent: computeCoveragePercent(overallReviewedParticles, overallTotalParticles),
      reviewRoundCount,
    },
    bySubject,
  };
}

export async function loadParticleCoverageStats(userId: string): Promise<ParticleCoverageSnapshot> {
  try {
    const params = new URLSearchParams({ userId });
    const response = await apiClient.requestJson<{
      success: boolean;
      data?: ParticleCoverageSnapshot;
      error?: string;
    }>(`/api/review/learning-episodes/particle-coverage?${params.toString()}`, { auth: true });

    if (response.success && response.data) {
      return normalizeCoverageSnapshot(response.data);
    }
  } catch {
    // Fall back to client-side aggregation when the API is unavailable.
  }

  return loadParticleCoverageFallback(userId);
}

export function formatCoveragePercent(value: number): string {
  return `${Math.max(0, Math.min(100, Math.round(value)))}%`;
}

export function getSubjectCoverageLabel(subject: SubjectCode): string {
  return SUBJECT_CONFIG[subject].label;
}
